import { useEffect, useRef, useState } from "react"
import { textOf } from "./drawingSettings.ts"
import type { TextEditRequest } from "./PaneDrawings.ts"

/** The inline editor TradingView opens when a text tool is placed, or a drawing that shows text is double-clicked. */
export function TextEditor({ onCancel, onDone, request }: {
  readonly request: TextEditRequest
  readonly onDone: (text: string) => void
  readonly onCancel: () => void
}) {
  const initial = textOf(request.drawing)
  const [text, setText] = useState(initial)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Only a blur after the editor had focus ends the edit.
  const focused = useRef(false)

  // The drawing library places a text tool on pointerdown and asks for the editor
  // then; that click's mousedown moves focus to the chart afterwards. Focusing
  // right away would lose focus at once (closing the editor empty, and sending
  // the typing to the symbol search). The mousedown is dispatched in the same
  // task as the pointerdown, so focusing in the next task comes after it.
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
    return () => clearTimeout(timer)
  }, [])

  return (
    <textarea
      ref={inputRef}
      className="drawing-text-editor"
      aria-label="Drawing text"
      style={{ left: request.at.x, top: request.at.y }}
      value={text}
      rows={Math.max(1, text.split("\n").length)}
      onChange={(event) => setText(event.target.value)}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        if (focused.current) onDone(text)
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel()
        else if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault()
          onDone(text)
        }
      }}
    />
  )
}
